import { IItemResponseWithSuccess, IUseFetchOptionsParams } from '@common/api.models';
import { FETCH_QUERIES_NAME } from '@common/constants';
import {
  createUseFetchOptionsCustomPropsWithSearchFn,
  createUseFetchOptionsWithSearchFn,
} from '@common/selectOptions.services';
import { isPositiveNumber } from '@core/Primitives/number.services';
import { isNullOrWhiteSpace } from '@gcl/react-core';
import { useQuery } from '@tanstack/react-query';
import { IProjectUserProfileOption } from '@userProfile/projectUserProfile.models';
import { AxiosErrorResponse } from '@core/error.models';
import { IMyDataRequest, IMyDataResponse } from './user.models';
import {
  getRseOptionsWithSearchAsync,
  searchUserInfoAsync,
  getMyDataAsync,
  getUserDistributionOptionsWithSearchAsync,
  IGetUserDistributionOptionsWithSearchProps,
  getUserAvatarAsync,
} from './user.services';

export const useFetchRseOptionsWithSearch = ({
  isDirty = false,
  shouldConvertOptions = false,
}: IUseFetchOptionsParams) =>
  createUseFetchOptionsWithSearchFn({
    queryName: FETCH_QUERIES_NAME.RSE_OPTIONS,
    getOptionsWithSearchFn: getRseOptionsWithSearchAsync,
    handleOptionsProps: { shouldConvertOptions },
    enabled: !isDirty,
  });

export const useSearchUserInfoAsync = (keyword: string, projectId?: number, enabled = true) => {
  return useQuery(
    [projectId, FETCH_QUERIES_NAME.SEARCH_USER_INFO, keyword],
    async () => {
      const results = await searchUserInfoAsync({ keyword, projectId });
      return results.item;
    },
    {
      enabled: !isNullOrWhiteSpace(keyword) && (projectId ? isPositiveNumber(projectId) : true) && !!enabled,
    },
  );
};

export const useGetMyDataAsync = (props: IMyDataRequest, enabled = true) => {
  return useQuery<IItemResponseWithSuccess<IMyDataResponse>, AxiosErrorResponse>(
    [props.projectId, FETCH_QUERIES_NAME.MY_DATA],
    async () => {
      const result = await getMyDataAsync(props);
      return result;
    },
    {
      enabled,
    },
  );
};

export const useFetchUserDistributionOptionsWithSearch = ({
  isDirty = false,
  shouldConvertOptions = false,
}: IUseFetchOptionsParams) =>
  createUseFetchOptionsCustomPropsWithSearchFn<IProjectUserProfileOption, IGetUserDistributionOptionsWithSearchProps>({
    queryName: FETCH_QUERIES_NAME.USER_DISTRIBUTION_OPTIONS_SEARCH,
    getOptionsWithSearchFn: getUserDistributionOptionsWithSearchAsync,
    handleOptionsProps: { shouldConvertOptions },
    enabled: !isDirty,
  });

export const useFetchUserAvatar = (uid: string, enabled = true) => {
  return useQuery(
    [uid, FETCH_QUERIES_NAME.USER_AVATAR],
    async () => {
      const result = await getUserAvatarAsync(uid);
      return result;
    },
    {
      enabled: enabled && !isNullOrWhiteSpace(uid),
    },
  );
};
